import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { StatusBadge } from '@/features/report/reconcile/parts'
import { formatDateTime } from '@/lib/format'
import { OVERRIDE_STATUSES, type OverrideStatus, type ReconcileRecord } from '@/lib/kantar-dia'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'
import { useAuthStore } from '@/stores/auth-store'

const t = messages.reconcile
const o = t.override

/** Durum seçicisinde "durum değişmesin, yalnız açıklama" değeri. */
const KEEP = 'keep'
type Choice = OverrideStatus | typeof KEEP

const CHOICE_ITEMS = [
  { value: KEEP, label: o.keepStatus },
  ...OVERRIDE_STATUSES.map((status) => ({ value: status, label: t.status[status] })),
]

/**
 * Kaydın elle düzeltmesi: mevcut düzeltme bilgisi ve `report.rpr183:edit` izni olana form.
 * Kaynak verisi değişmez; düzeltme backend'de saklanır ve denetim kaydına yazılır.
 */
export function OverrideSection({ record }: { record: ReconcileRecord }) {
  const queryClient = useQueryClient()
  const canEdit = useAuthStore((state) => state.hasPermission('report.rpr183:edit'))
  const current = record.override
  const [choice, setChoice] = useState<Choice>(current?.status ?? KEEP)
  const [note, setNote] = useState(current?.note ?? '')
  const [saved, setSaved] = useState(false)
  const savedTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(savedTimer.current), [])
  const editable = record.tartino !== null && record.tartino !== undefined

  const onDone = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.reconcileOverrides() })
    setSaved(true)
    clearTimeout(savedTimer.current)
    savedTimer.current = setTimeout(() => setSaved(false), 2000)
  }
  const save = useMutation({
    mutationFn: () =>
      services.reconcile.setOverride(record.kantarAdi, record.tartino, {
        status: choice === KEEP ? null : choice,
        note: note.trim(),
      }),
    onSuccess: onDone,
  })
  const remove = useMutation({
    mutationFn: () => services.reconcile.deleteOverride(record.kantarAdi, record.tartino),
    onSuccess: () => {
      setChoice(KEEP)
      setNote('')
      onDone()
    },
  })
  const error = save.error ?? remove.error

  return (
    <div className="flex flex-col gap-3 p-3 text-sm sm:p-4">
      {current ? (
        <div className="flex flex-col gap-1">
          <p className="text-muted-foreground text-xs">
            {o.by(current.updatedByName ?? t.unknown, formatDateTime(current.updatedAt))}
          </p>
          {current.status && record.computedStatus && (
            <p className="flex flex-wrap items-center gap-1.5 text-xs">
              {o.computed}
              <StatusBadge status={record.computedStatus} />
            </p>
          )}
          {!canEdit && <p className="whitespace-pre-wrap">{current.note}</p>}
        </div>
      ) : (
        !canEdit && <p className="text-muted-foreground">{o.none}</p>
      )}

      {canEdit && !editable && <p className="text-muted-foreground">{o.noTartino}</p>}
      {canEdit && editable && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`override-status-${record.key}`}>{o.status}</Label>
            <Select
              value={choice}
              items={CHOICE_ITEMS}
              onValueChange={(value) => setChoice(value as Choice)}
            >
              <SelectTrigger id={`override-status-${record.key}`} className="w-full sm:w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {CHOICE_ITEMS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`override-note-${record.key}`}>{o.note}</Label>
            <Textarea
              id={`override-note-${record.key}`}
              value={note}
              maxLength={1000}
              placeholder={o.notePlaceholder}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={!note.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              {o.save}
            </Button>
            {current && (
              <Button
                size="sm"
                variant="outline"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(o.removeConfirm)) remove.mutate()
                }}
              >
                {o.remove}
              </Button>
            )}
            {saved && <span className="text-positive text-xs">{o.saved}</span>}
            {error && <span className="text-destructive text-xs">{o.failed}</span>}
          </div>
        </div>
      )}
    </div>
  )
}
